/* eslint-disable @typescript-eslint/no-unused-vars */
import React from 'react';
import { chevronDown } from '../../../assets/svg';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { RangeStatus } from '../../../constants';
import { DateRanges } from '../../../types';

export const DateRangePicker = ({
  position,
  showPredefined,
  reset,
  handleFilter,
}: {
  position?: string;
  reset?: () => void;
  showPredefined?: boolean;
  handleFilter: (
    range?: DateRanges,
    dates?: {
      startDate: Date | null;
      endDate: Date | null;
    }
  ) => void;
}) => {
  const [filterOpen, setFilterOpen] = React.useState(false);
  const [range, setRange] = React.useState(
    !showPredefined ? 'CUSTOM' : RangeStatus[0].value
  );
  const [date, setDate] = React.useState<{
    startDate: Date | null;
    endDate: Date | null;
  }>({
    startDate: new Date(),
    endDate: null,
  });

  const _toggleFilter = () => {
    setFilterOpen((prev) => !prev);
  };

  const resetFilter = () => {
    setDate({ startDate: new Date(), endDate: null });
    reset && reset();
  };

  const onCalenderChange = (dates: [Date | null, Date | null]) => {
    const [start, end] = dates;
    setDate({ ...date, startDate: start, endDate: end });

    if (start && end) {
      setRange('CUSTOM');
      handleFilter(undefined, { startDate: start, endDate: end });

      _toggleFilter();
    }
  };

  const onFilterChange = (value: DateRanges) => {
    setRange(value);
    handleFilter(value);

    setDate({
      startDate: new Date(),
      endDate: null,
    });

    _toggleFilter();
  };

  return (
    <>
      {filterOpen && (
        <div
          className="fixed inset-0 z-30"
          onClick={_toggleFilter}
        />
      )}

      <div className="relative z-30 overflow-visible">
        <button
          className="appearance-none px-4 py-2.5 rounded-lg border border-slate-medium bg-white flex justify-between items-center gap-3 relative z-20 w-full text-sm text-text hover:bg-gray-50 transition-colors focus:outline-none focus:border-primary"
          onClick={_toggleFilter}
        >
          <span className="whitespace-nowrap">
            {range === 'CUSTOM'
              ? date.startDate && date.endDate
                ? `${date.startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} - ${date.endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
                : new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
              : RangeStatus.filter(({ value }) => value === range)[0].option}
          </span>
          <svg width={12} height={12} viewBox="0 0 12 12" fill="none" className="flex-shrink-0">
            <path d="M3 4.5L6 7.5L9 4.5" stroke="#A5A7AC" strokeWidth={1.2} strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>

        {filterOpen && (
          <div
            className={`mt-5  sm:mt-0 sm:absolute flex flex-col items-center sm:flex-row p-2 rounded shadow border border-grey-2 bg-white space-x-2 z-40 ${
              position ?? 'left-5 top-10'
            } ${showPredefined ? 'sm:w-96' : ''}`}
          >
            <div>
              <DatePicker
                selected={date.startDate}
                onChange={onCalenderChange}
                startDate={date.startDate}
                endDate={date.endDate}
                filterDate={(date) => {
                  return new Date() > date;
                }}
                selectsRange
                inline
              />
              {date.startDate && date.endDate && (
                <div className="text-xs mb-1">
                  <p className="text-neutral-700">
                    {date.startDate?.toDateString()} -{' '}
                    {date.endDate?.toDateString()}
                  </p>
                  <button
                    className="underline text-primary-700 mt-1"
                    onClick={resetFilter}
                  >
                    Reset
                  </button>
                </div>
              )}
            </div>

            {showPredefined && (
              <div className="sm:w-4/12 sm:space-y-1 flex justify-center sm:flex-col flex-wrap">
                {RangeStatus.map((predefinedRange) => (
                  <button
                    className={`px-3 py-1 rounded text-sm text-left mr-3 mt-2 sm:mt-0 sm:mr-0 semibold ${
                      range === predefinedRange.value
                        ? 'bg-slate-400 text-white'
                        : 'bg-slate-200'
                    }`}
                    onClick={() => onFilterChange(predefinedRange.value)}
                    key={predefinedRange.value}
                  >
                    {predefinedRange.option}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};
